import type { CustomLayerInterface, Map as MapLibreMap } from "maplibre-gl";
import { chooseWindGrid, sampleWind, speedKmh, windSpeedColor, type WindField } from "../shared/wind-field";
import { loadOpenMeteoWindField } from "./wind-field";

/** Hours of model time per frame — short strokes, not wormy polylines. */
const STEP_H = 0.016;
const FILL_A = 0.42;
const FADE = "rgba(0,0,0,0.86)";

type Particle = { lon: number; lat: number; age: number; life: number };

export type WindLayerHandle = {
  setEnabled: (on: boolean) => void;
};

/** Offscreen 2d → WebGL quad under heat. Fill is a smooth sample; streaks are fade-trails. */
export function installWindLayer(map: MapLibreMap): WindLayerHandle {
  const layer = new WindParticles();
  map.addLayer(layer, "heat");
  return {
    setEnabled: (on) => layer.setEnabled(on),
  };
}

class WindParticles implements CustomLayerInterface {
  id = "wind";
  type = "custom" as const;
  renderingMode = "2d" as const;

  private map: MapLibreMap | null = null;
  private field: WindField | null = null;
  private enabled = false;
  private moving = false;
  private particles: Particle[] = [];
  private paper: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private fillPaper: HTMLCanvasElement | null = null;
  private fillCtx: CanvasRenderingContext2D | null = null;
  private trailPaper: HTMLCanvasElement | null = null;
  private trailCtx: CanvasRenderingContext2D | null = null;
  private viewKey = "";
  private fillKey = "";
  private raf = 0;
  private lastFrame = 0;
  private moveTimer = 0;
  private texReady = false;
  private fetchGen = 0;
  private onVis: (() => void) | null = null;
  private onMoveStart: (() => void) | null = null;
  private onMoveEnd: (() => void) | null = null;
  private program: WebGLProgram | null = null;
  private buf: WebGLBuffer | null = null;
  private tex: WebGLTexture | null = null;
  private aPos = 0;
  private uTex: WebGLUniformLocation | null = null;

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) {
      this.stop();
      this.field = null;
      this.particles = [];
    } else {
      void this.refreshField();
      this.kick();
    }
    this.map?.triggerRepaint();
  }

  onAdd(map: MapLibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    this.map = map;
    this.paper = document.createElement("canvas");
    this.ctx = this.paper.getContext("2d");
    this.fillPaper = document.createElement("canvas");
    this.fillCtx = this.fillPaper.getContext("2d");
    this.trailPaper = document.createElement("canvas");
    this.trailCtx = this.trailPaper.getContext("2d");
    const vs = compile(gl, gl.VERTEX_SHADER, `attribute vec2 a_pos; varying vec2 v_uv;
      void main() { v_uv = vec2(a_pos.x * 0.5 + 0.5, 0.5 - a_pos.y * 0.5); gl_Position = vec4(a_pos, 0.0, 1.0); }`);
    const fs = compile(gl, gl.FRAGMENT_SHADER, `precision mediump float; uniform sampler2D u_tex; varying vec2 v_uv;
      void main() { gl_FragColor = texture2D(u_tex, v_uv); }`);
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    this.program = prog;
    this.aPos = gl.getAttribLocation(prog, "a_pos");
    this.uTex = gl.getUniformLocation(prog, "u_tex");
    this.buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    this.tex = gl.createTexture();
    this.onVis = () => {
      if (document.hidden) this.stop();
      else this.kick();
    };
    this.onMoveStart = () => {
      this.moving = true;
      this.clearTrails();
    };
    this.onMoveEnd = () => {
      this.moving = false;
      this.viewKey = "";
      this.fillKey = "";
      if (!this.enabled) return;
      // Debounce so a wheel-zoom burst is one Open-Meteo POST, not one per tick.
      if (this.moveTimer) window.clearTimeout(this.moveTimer);
      this.moveTimer = window.setTimeout(() => {
        this.moveTimer = 0;
        if (!this.enabled) return;
        this.seedAll();
        void this.refreshField();
      }, 300);
    };
    document.addEventListener("visibilitychange", this.onVis);
    map.on("movestart", this.onMoveStart);
    map.on("moveend", this.onMoveEnd);
  }

  onRemove(map: MapLibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    this.stop();
    if (this.moveTimer) window.clearTimeout(this.moveTimer);
    if (this.onVis) document.removeEventListener("visibilitychange", this.onVis);
    if (this.onMoveStart) map.off("movestart", this.onMoveStart);
    if (this.onMoveEnd) map.off("moveend", this.onMoveEnd);
    if (this.program) gl.deleteProgram(this.program);
    if (this.buf) gl.deleteBuffer(this.buf);
    if (this.tex) gl.deleteTexture(this.tex);
    this.map = null;
  }

  render(gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    if (!this.enabled || !this.program || !this.paper || !this.ctx || !this.map) return;
    if (this.field && !this.moving) this.stepAndTrail();
    this.composite();
    gl.useProgram(this.program);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
    gl.enableVertexAttribArray(this.aPos);
    gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    // Filter/wrap once — these do not change per frame.
    if (!this.texReady) {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      this.texReady = true;
    }
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.paper);
    gl.uniform1i(this.uTex, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  }

  private async refreshField(): Promise<void> {
    const map = this.map;
    if (!map || !this.enabled) return;
    const b = map.getBounds();
    const spec = chooseWindGrid(
      { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() },
      map.getZoom(),
    );
    const gen = ++this.fetchGen;
    // Viewport POST first. Worker GET is the coarse Europe fallback only.
    let field = await loadOpenMeteoWindField(spec);
    if (!field) {
      try {
        const res = await fetch("/api/wind-field");
        if (res.ok) field = (await res.json()) as WindField;
      } catch {
        field = null;
      }
    }
    if (gen !== this.fetchGen || !this.enabled) return;
    if (!field?.u?.length) return;
    this.field = field;
    this.fillKey = "";
    this.seedAll();
    this.kick();
    map.triggerRepaint();
  }

  private kick(): void {
    if (!this.enabled || !this.map) return;
    this.stop();
    // 30 fps is enough to read flow; 60 fps kept the whole MapLibre map from idling.
    const frameMs = 1000 / 30;
    const tick = (now: number) => {
      if (!this.enabled) return;
      if (now - this.lastFrame >= frameMs) {
        this.lastFrame = now;
        this.map?.triggerRepaint();
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  private stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private particleCount(): number {
    const map = this.map;
    if (!map) return 280;
    const c = map.getCanvas();
    // Cap lower than the first pass — enough to read flow, not a swarm on retina.
    return Math.max(160, Math.min(480, Math.round((c.clientWidth * c.clientHeight) / 1800)));
  }

  private seedAll(): void {
    const n = this.particleCount();
    this.particles = Array.from({ length: n }, () => this.spawn());
  }

  private spawn(): Particle {
    const map = this.map;
    if (!map) return { lon: 0, lat: 50, age: 0, life: 80 };
    const b = map.getBounds();
    return {
      lon: b.getWest() + Math.random() * (b.getEast() - b.getWest()),
      lat: b.getSouth() + Math.random() * (b.getNorth() - b.getSouth()),
      age: 0,
      life: 28 + Math.floor(Math.random() * 50),
    };
  }

  private stepAndTrail(): void {
    const map = this.map;
    const field = this.field;
    const tctx = this.trailCtx;
    const trails = this.trailPaper;
    if (!map || !field || !tctx || !trails) return;
    const src = map.getCanvas();
    const dpr = src.width / Math.max(1, src.clientWidth);
    this.ensureSize();
    // Fade last frame — that is what makes hair-like streamlines instead of polylines.
    tctx.setTransform(1, 0, 0, 1, 0, 0);
    tctx.globalCompositeOperation = "destination-in";
    tctx.fillStyle = FADE;
    tctx.fillRect(0, 0, trails.width, trails.height);
    tctx.globalCompositeOperation = "source-over";
    tctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    tctx.lineCap = "round";
    tctx.lineWidth = 1.7;
    // Longer advection at continent zoom so a frame is more than a sub-pixel dash.
    const stepH = STEP_H * Math.max(0.55, Math.min(2.4, 5.4 / Math.max(2, map.getZoom())));
    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i]!;
      const { u, v } = sampleWind(field, p.lon, p.lat);
      const spd = speedKmh(u, v);
      p.age += 1;
      if (p.age > p.life || spd < 1.5) {
        this.particles[i] = this.spawn();
        continue;
      }
      const a = map.project([p.lon, p.lat]);
      const cos = Math.cos((p.lat * Math.PI) / 180);
      p.lon += (u * stepH) / (111.32 * Math.max(0.2, cos));
      p.lat += (v * stepH) / 110.54;
      const b = map.project([p.lon, p.lat]);
      const [cr, cg, cb] = windSpeedColor(spd);
      const t = Math.min(1, spd / 65);
      tctx.strokeStyle = `rgba(${Math.min(255, cr + 90)},${Math.min(255, cg + 90)},${Math.min(255, cb + 90)},${0.38 + 0.48 * t})`;
      tctx.beginPath();
      tctx.moveTo(a.x, a.y);
      tctx.lineTo(b.x, b.y);
      tctx.stroke();
    }
  }

  private composite(): void {
    const map = this.map;
    const ctx = this.ctx;
    const paper = this.paper;
    if (!map || !ctx || !paper) return;
    this.ensureSize();
    this.ensureFill();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, paper.width, paper.height);
    if (this.fillPaper) {
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(this.fillPaper, 0, 0, paper.width, paper.height);
    }
    if (this.trailPaper && !this.moving) ctx.drawImage(this.trailPaper, 0, 0);
  }

  private ensureSize(): void {
    const map = this.map;
    if (!map || !this.paper || !this.trailPaper) return;
    const src = map.getCanvas();
    if (this.paper.width === src.width && this.paper.height === src.height) return;
    this.paper.width = src.width;
    this.paper.height = src.height;
    this.trailPaper.width = src.width;
    this.trailPaper.height = src.height;
    this.viewKey = "";
    this.fillKey = "";
  }

  private ensureFill(): void {
    const map = this.map;
    const field = this.field;
    const fctx = this.fillCtx;
    const fill = this.fillPaper;
    if (!map || !field || !fctx || !fill) return;
    const src = map.getCanvas();
    const c = map.getCenter();
    const key = `${c.lng.toFixed(3)}|${c.lat.toFixed(3)}|${map.getZoom().toFixed(2)}|${src.width}|${field.observed_at}`;
    if (key === this.fillKey) return;
    this.fillKey = key;
    // Third-res sample, then scale up — bilinear on screen, not one rectangle per model cell.
    const step = 3;
    const w = Math.max(2, Math.ceil(src.clientWidth / step));
    const h = Math.max(2, Math.ceil(src.clientHeight / step));
    if (fill.width !== w || fill.height !== h) {
      fill.width = w;
      fill.height = h;
    }
    const img = fctx.createImageData(w, h);
    const data = img.data;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const ll = map.unproject([(x + 0.5) * step, (y + 0.5) * step]);
        const { u, v } = sampleWind(field, ll.lng, ll.lat);
        const i = (y * w + x) * 4;
        if (u === 0 && v === 0 && !insideField(field, ll.lng, ll.lat)) {
          data[i + 3] = 0;
          continue;
        }
        const [r, g, b] = windSpeedColor(speedKmh(u, v));
        data[i] = r;
        data[i + 1] = g;
        data[i + 2] = b;
        data[i + 3] = Math.round(FILL_A * 255);
      }
    }
    fctx.putImageData(img, 0, 0);
  }

  private clearTrails(): void {
    const t = this.trailCtx;
    const p = this.trailPaper;
    if (!t || !p) return;
    t.setTransform(1, 0, 0, 1, 0, 0);
    t.clearRect(0, 0, p.width, p.height);
  }
}

function insideField(field: WindField, lon: number, lat: number): boolean {
  return lon >= field.west && lon <= field.east && lat >= field.south && lat <= field.north;
}

function compile(gl: WebGLRenderingContext | WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  return sh;
}
